/**
 * Действия DevOps из карточки задачи: поднять и погасить фича-стенд,
 * переключить рантайм-флаг, собрать релиз.
 *
 * Стенд — это namespace, а не запись в базе трекера: что в нём живо, по каким
 * адресам открывается и когда снесётся само, знает GitLab, и ручка стендов
 * спрашивает у него. Поэтому и поднимают, и гасят стенд целиком — составом
 * задачи и по номеру, а не по паре «репозиторий и ветка» (DEV-1627).
 */
import { devopsApi, gitlabApi } from '@/api'

import type {
  V1DevOpsAction,
  V1FeatureStand,
  V1FeatureStandService,
  V1IssueService,
  V1RuntimeFlag,
} from '@/api/clients/tracker-gitlab'

/** Среда рантайм-флага. Других у инфраструктуры нет. */
export type FlagEnvironment = 'dev' | 'prod'

export const FLAG_ENVIRONMENTS: FlagEnvironment[] = ['dev', 'prod']

/**
 * Флаг в одной среде. Сервис хранит запись на пару «ключ и среда»: один и тот
 * же флаг на dev и prod — это две записи, и включены они бывают по-разному.
 */
export interface RuntimeFlag {
  key: string
  description: string
  environment: FlagEnvironment
  enabled: boolean
  updatedBy: string
  updatedAt: string
}

/** Флаг обеими средами сразу — как его показывает таблица макета */
export interface FlagRow {
  key: string
  description: string
  enabled: Record<FlagEnvironment, boolean>
  updatedBy: string
  updatedAt: string
}

/** Действие DevOps в журнале: кто, что и чем закончилось */
export interface DevOpsAction {
  id: string
  issueId: string
  issueKey: string
  issueTitle: string
  type: string
  projectPath: string
  target: string
  /** Параметры вызова строкой JSON: среда и значение флага, ветка релиза */
  paramsJson: string
  status: string
  actor: string
  pipelineUrl: string
  error: string
  createdAt: string
  finishedAt: string
}

/** Типы записей журнала — те же строки, что отдаёт сервис */
export const ACTION_TYPES = {
  standUp: 'DEVOPS_ACTION_TYPE_FEATURE_ENV_CREATE',
  standDown: 'DEVOPS_ACTION_TYPE_FEATURE_ENV_DELETE',
  flagSet: 'DEVOPS_ACTION_TYPE_RUNTIME_FLAG_SET',
  release: 'DEVOPS_ACTION_TYPE_RELEASE_CREATE',
} as const

export const ACTION_STATUS = {
  pending: 'DEVOPS_ACTION_STATUS_PENDING',
  success: 'DEVOPS_ACTION_STATUS_SUCCESS',
  failed: 'DEVOPS_ACTION_STATUS_FAILED',
} as const

/** Сервис в стенде: с какой ветки приехал и по какому адресу открывается */
export interface StandService {
  projectPath: string
  /** Имя окружения в GitLab: `feature/DEV-1421-kanban-board` */
  environment: string
  branch: string
  /** Пусто, пока сборка не дошла до выката: окружение заводится уже на сборке */
  url: string
}

/**
 * Живой стенд: namespace `vp-f-dev-1421` со всем, что в него выкачено.
 *
 * Номер стенда — он же ключ задачи, если стенд поднимали из неё. Один стенд —
 * одна строка таблицы, сколько бы сервисов в нём ни жило.
 */
export interface FeatureStand {
  name: string
  /** Адреса, по которым стенд открывается: по одному на выкаченный фронт и API */
  urls: string[]
  services: StandService[]
  issueId: string
  issueKey: string
  issueTitle: string
  actor: string
  raisedAt: string
  /** Когда стенд снесёт себя сам, если его не пересобрать */
  autoStopAt: string
}

/**
 * Сервис задачи: репозиторий, который поедет в её стенд.
 *
 * Подсказка (`suggested`) — репозиторий, которого в списке нет, но ветка задачи
 * там уже живёт. В стенд она сама не идёт: состав задаёт человек.
 */
export interface IssueService {
  projectId: string
  path: string
  suggested: boolean
}

/** Переключение флага из журнала — строка блока «последние переключения» */
export interface FlagChange {
  id: string
  key: string
  environment: FlagEnvironment
  enabled: boolean
  actor: string
  at: string
}

/** Релиз из журнала: тег сервиса и чем закончилась его постановка */
export interface Release {
  id: string
  projectPath: string
  tag: string
  actor: string
  status: string
  pipelineUrl: string
  issueKey: string
  at: string
}

const flagOf = (flag: V1RuntimeFlag): RuntimeFlag => ({
  key: flag.key ?? '',
  description: flag.description ?? '',
  environment: flag.environment === 'prod' ? 'prod' : 'dev',
  enabled: flag.enabled ?? false,
  updatedBy: flag.updatedBy ?? '',
  updatedAt: flag.updatedAt ?? '',
})

const actionOf = (action: V1DevOpsAction): DevOpsAction => ({
  id: action.id ?? '',
  issueId: action.issueId ?? '',
  issueKey: action.issueKey ?? '',
  issueTitle: action.issueTitle ?? '',
  type: String(action.actionType ?? ''),
  projectPath: action.gitlabProjectPath ?? '',
  target: action.target ?? '',
  paramsJson: action.paramsJson ?? '',
  status: String(action.status ?? ''),
  actor: action.actorName ?? '',
  pipelineUrl: action.pipelineUrl ?? '',
  error: action.error ?? '',
  createdAt: action.createdAt ?? '',
  finishedAt: action.finishedAt ?? '',
})

const standServiceOf = (service: V1FeatureStandService): StandService => ({
  projectPath: service.gitlabProjectPath ?? '',
  environment: service.environment ?? '',
  branch: service.branch ?? '',
  url: service.url ?? '',
})

const standOf = (stand: V1FeatureStand): FeatureStand => ({
  name: stand.name ?? '',
  urls: stand.urls ?? [],
  services: (stand.services ?? []).map(standServiceOf),
  issueId: stand.issueId ?? '',
  issueKey: stand.issueKey ?? '',
  issueTitle: stand.issueTitle ?? '',
  actor: stand.actorName ?? '',
  raisedAt: stand.raisedAt ?? '',
  autoStopAt: stand.autoStopAt ?? '',
})

/** Живые стенды — их знает GitLab, а не журнал трекера */
export const listStands = async (): Promise<FeatureStand[]> => {
  const { data } = await devopsApi.devOpsServiceListFeatureStands()

  return (data.stands ?? []).map(standOf)
}

/**
 * Поднять стенд составом задачи. Пустой список репозиториев — весь состав, и
 * это обычный случай: перечисление нужно для повторного подъёма части.
 */
export const raiseStand = async (issueId: string, projectPaths: string[] = []): Promise<void> => {
  await devopsApi.devOpsServiceRaiseFeatureStand({ issueId, gitlabProjectPaths: projectPaths })
}

/**
 * Погасить стенд целиком — namespace общий на все сервисы, и снос по одному
 * репозиторию оставил бы соседей показывать стенд, которого нет.
 */
export const stopStand = async (stand: string): Promise<void> => {
  await devopsApi.devOpsServiceStopFeatureStand({ stand })
}

/** Сервисы задачи: состав её стенда плюс подсказки из веток */
export const listIssueServices = async (issueId: string): Promise<IssueService[]> => {
  const { data } = await gitlabApi.gitLabServiceListIssueServices(issueId)

  return (data.services ?? []).map((service: V1IssueService) => ({
    projectId: service.gitlabProjectId ?? '',
    path: service.gitlabProjectPath ?? '',
    suggested: Boolean(service.suggested),
  }))
}

/** Задать состав задачи целиком: список замещает прежний */
export const setIssueServices = async (issueId: string, projectIds: string[]): Promise<void> => {
  await gitlabApi.gitLabServiceSetIssueServices(issueId, { gitlabProjectIds: projectIds })
}

export const listFlags = async (environment?: FlagEnvironment): Promise<RuntimeFlag[]> => {
  const { data } = await devopsApi.devOpsServiceListRuntimeFlags(environment)

  return (data.flags ?? []).map(flagOf)
}

/**
 * Флаги строками таблицы: записи одного ключа из разных сред сходятся в одну
 * строку с парой тумблеров, как в макете.
 */
export const toFlagRows = (flags: RuntimeFlag[]): FlagRow[] => {
  const rows = new Map<string, FlagRow>()

  for (const flag of flags) {
    const row = rows.get(flag.key) ?? {
      key: flag.key,
      description: flag.description,
      enabled: { dev: false, prod: false },
      updatedBy: flag.updatedBy,
      updatedAt: flag.updatedAt,
    }

    row.enabled[flag.environment] = flag.enabled
    // Показываем самую свежую правку из обеих сред: строка одна, и «когда
    // трогали последний раз» у неё тоже одно.
    if (flag.updatedAt > row.updatedAt) {
      row.updatedAt = flag.updatedAt
      row.updatedBy = flag.updatedBy
    }

    if (!row.description) row.description = flag.description

    rows.set(flag.key, row)
  }

  return [...rows.values()]
}

export const setFlag = async (
  key: string,
  environment: FlagEnvironment,
  enabled: boolean,
): Promise<void> => {
  await devopsApi.devOpsServiceSetRuntimeFlag(key, { environment, enabled })
}

/**
 * До каких репозиториев дошли задачи эпика. Сервер отвечает подсказкой, а
 * решает человек: собирать релиз или нет, видно только ему.
 */
export const listReleaseServices = async (epicId: string): Promise<string[]> => {
  const { data } = await devopsApi.devOpsServiceListReleaseServices(epicId)

  return data.gitlabProjectPaths ?? []
}

/** Черновик описания релиза и то, из чего ассистент его собрал */
export interface ReleaseNotes {
  description: string
  previousTag: string
  commits: number
}

/**
 * Описание релиза, написанное ассистентом по хвосту коммитов после последнего
 * тега. Черновик: тег ставит человек, и текст перед этим он читает — поэтому
 * ответ несёт ещё и то, из чего собран.
 */
export const generateReleaseNotes = async (projectPath: string): Promise<ReleaseNotes> => {
  const { data } = await devopsApi.devOpsServiceGenerateReleaseNotes({
    gitlabProjectPath: projectPath,
  })

  return {
    description: data.description ?? '',
    previousTag: data.previousTag ?? '',
    commits: data.commitCount ?? 0,
  }
}

export const createRelease = async (
  projectPath: string,
  tag: string,
  ref: string,
  description: string,
  issueId?: string,
): Promise<void> => {
  await devopsApi.devOpsServiceCreateRelease({
    gitlabProjectPath: projectPath,
    tag,
    ref,
    description,
    issueId,
  })
}

/**
 * Журнал действий: чем закончился каждый запуск. Без задачи в запросе это
 * сквозной журнал раздела — сервис сам сужает его до проектов, куда
 * запросивший допущен.
 */
export const listActions = async (issueId?: string, perPage = 100): Promise<DevOpsAction[]> => {
  const { data } = await devopsApi.devOpsServiceListDevOpsActions(issueId, '1', String(perPage))

  return (data.actions ?? []).map(actionOf)
}

/** Переключения флагов из журнала: среда и значение лежат в параметрах вызова. */
export const toFlagChanges = (actions: DevOpsAction[]): FlagChange[] =>
  actions
    .filter((action) => action.type === ACTION_TYPES.flagSet)
    .map((action) => {
      const params = parseParams(action)

      return {
        id: action.id,
        key: action.target,
        environment: params.environment === 'prod' ? ('prod' as const) : ('dev' as const),
        enabled: Boolean(params.enabled),
        actor: action.actor,
        at: action.createdAt,
      }
    })

/**
 * Кто и когда трогал флаг последним — из журнала.
 *
 * Сам файл флагов этого не хранит: в нём пара «ключ — включён». Журнал знает,
 * потому что переключают флаг через трекер; правку, сделанную коммитом мимо
 * него, колонка законно не покажет.
 */
export const withLastChange = (rows: FlagRow[], changes: FlagChange[]): FlagRow[] => {
  const latest = new Map<string, FlagChange>()

  // Журнал идёт от свежих к старым — первая встреченная запись и есть последняя.
  for (const change of changes) {
    if (!latest.has(change.key)) latest.set(change.key, change)
  }

  return rows.map((row) => {
    const change = latest.get(row.key)

    return change ? { ...row, updatedBy: change.actor, updatedAt: change.at } : row
  })
}

/** Релизы из журнала, свежие первыми */
export const toReleases = (actions: DevOpsAction[]): Release[] =>
  actions
    .filter((action) => action.type === ACTION_TYPES.release)
    .map((action) => ({
      id: action.id,
      projectPath: action.projectPath,
      tag: action.target,
      actor: action.actor,
      status: action.status,
      pipelineUrl: action.pipelineUrl,
      issueKey: action.issueKey,
      at: action.createdAt,
    }))

/**
 * Параметры вызова лежат строкой JSON. Битую строку глотаем: журнал ведёт
 * сервис, и ради одной непрочитанной записи весь блок ронять незачем.
 */
const parseParams = (action: DevOpsAction): { environment?: string; enabled?: boolean } => {
  try {
    return JSON.parse(action.paramsJson || '{}') as { environment?: string; enabled?: boolean }
  } catch {
    return {}
  }
}
